<script setup>
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Services from '../services'

const emit = defineEmits(['close'])

const { t, locale } = useI18n()
const model = defineModel()
const calendar = ref()
const modelCalendar = ref([])
const focusDate = ref(null)
const props = defineProps({
  inline: {
    type: Boolean,
    default: false,
  },
})

const setCalendarModel = () => {
  model.value = modelCalendar.value.map((e) =>
    Services.formatter.dateToString(e),
  )

  calendar.value.toggle()
  emit('close')
}

const formatDate = (date) => {
  return date?.toLocaleString(locale.value, {
    year: 'numeric',
    month: 'long',
  })
}

const setCalendarPeriod = (type) => {
  const today = new Date()
  let startDate, stopDate

  switch (type) {
    case 'week':
      startDate = new Date()
      stopDate = new Date()
      startDate.setDate(today.getDate() - (today.getDay() - 1))
      stopDate.setDate(startDate.getDate() + 6)

      break
    case 'day':
      startDate = stopDate = today
      break
    case 'weekend':
      startDate = new Date()
      stopDate = new Date()
      startDate.setDate(today.getDate() - (today.getDay() - 6))
      stopDate.setDate(startDate.getDate() + 1)
      break
    default:
      break
  }

  modelCalendar.value = [startDate, stopDate]
  focusDate.value = modelCalendar.value[0]
}

const focusCalendar = (direction) => {
  const date = new Date(focusDate.value)
  date.setMonth(focusDate.value.getMonth() + direction)
  focusDate.value = date
}

const resetFilter = () => {
  modelCalendar.value = model.value = []
}

watch(
  model,
  () => {
    if (model.value?.length) {
      modelCalendar.value = model.value.map((e) => new Date(e))
    }

    focusDate.value = modelCalendar.value[0] || new Date()
  },
  { immediate: true },
)
</script>

<template>
  <b-datepicker
    ref="calendar"
    v-model="modelCalendar"
    v-bind="{
      inline: props.inline,
      'focused-date': focusDate,
      'mobile-native': false,
      locale: locale,
      'first-day-of-week': 1,
      'close-on-click': false,
      'nearby-selectable-month-days': false,
      range: true,
    }"
  >
    <template #trigger>
      <button
        type="button"
        class="button is-lighter is-large is-size-6 is-fullwidth"
      >
        <span>Calendar</span>
        <b-icon icon="caret-down-bold" class="ml-2" />
        <p
          v-if="model?.length"
          class="button is-xs is-black is-rounded is-square ml-4"
          @click.stop="resetFilter"
        >
          <b-icon icon="x" />
        </p>
      </button>
    </template>

    <template #header>
      <div class="buttons mb-0">
        <b-button
          type="is-lighter is-primary-hover"
          class="has-radius-small is-size-7"
          :label="t('index.week')"
          @click="setCalendarPeriod('week')"
        />

        <b-button
          type="is-lighter is-primary-hover"
          class="has-radius-small is-size-7"
          :label="t('index.day')"
          @click="setCalendarPeriod('day')"
        />

        <b-button
          type="is-lighter is-primary-hover"
          class="has-radius-small is-size-7"
          :label="t('index.weekend')"
          @click="setCalendarPeriod('weekend')"
        />
      </div>

      <div class="columns is-vcentered is-mobile is-gapless is-nav">
        <div class="column is-narrow">
          <button
            type="button"
            class="button is-white is-square"
            @click="focusCalendar(-1)"
          >
            <b-icon icon="caret-left-bold" />
          </button>
        </div>
        <div class="column has-text-centered">{{ formatDate(focusDate) }}</div>
        <div class="column is-narrow">
          <button
            type="button"
            class="button is-white is-square"
            @click="focusCalendar(+1)"
          >
            <b-icon icon="caret-right-bold" />
          </button>
        </div>
      </div>
    </template>

    <b-button
      type="is-primary"
      class="has-radius-small"
      expanded
      @click="setCalendarModel"
      :disabled="!modelCalendar.length"
    >
      {{ t('index.apply') }}
    </b-button>
  </b-datepicker>
</template>
